<script setup>
import dayjs from 'dayjs';
import { computed } from 'vue';
import verifiedIcon from '@/assets/verified-icon.svg';
import unverifiedIcon from '@/assets/unverified-icon.svg';
// import eldConnectedIcon from '@/assets/eld-connected-icon.svg';
// import eldNotConnectedIcon from '@/assets/eld-not-connected-icon.svg';
import insuredIcon from '@/assets/insured-icon.svg';
import coiNotFoundIcon from '@/assets/coi-not-found-icon.svg';
import { useScreenSize } from '@/composables';
import { useStore } from 'vuex';
import MyLoadsDesktopButtonComponent from '@/components/myLoads/MyLoadsDesktopButtonComponent.vue';

const { innerWidth } = useScreenSize();

const store = useStore();
const props = defineProps({
  equipment: {
    type: Object,
    default: () => ({}),
  },
  isModal: {
    type: Boolean,
    default: false,
  },
});

const isSetEldExemptFeatureEnabled = computed(
  () =>
    store.state.users.featureFlags
      ?.fe_carrier_set_carrier_vehicle_eld_exempt_status?.enabled
);

const emit = defineEmits(['resolveIssuesClick']);

const formattedLastSeen = computed(() =>
  dayjs(props.equipment.last_seen).format('MM/DD/YYYY')
);
</script>

<template>
  <!--     class="flex  flex-col gap-2 rounded-xl bg-white px-4 py-3 outline outline-1 outline-offset-[-1px] outline-neutral-300-a"
 -->
  <div
    v-if="innerWidth > 0"
    class="flex h-[285px] min-w-[360px] max-w-[600px] flex-1 flex-col rounded-xl border border-steel-40 bg-white"
    :class="
      innerWidth < 400 ? 'w-full' : innerWidth < 610 ? 'w-[400px]' : 'w-[285px]'
    "
  >
    <div
      class="flex items-center justify-between border-b border-steel-40 px-4 py-2"
    >
      <div class="flex items-center gap-1">
        <img
          class=""
          :src="equipment.verified ? verifiedIcon : unverifiedIcon"
          :alt="equipment.verified ? 'Verified Icon' : 'Unverified Icon'"
        />
        <span class="text-steel-1100">
          {{ equipment.verified ? 'Verified' : 'Unverified' }}
        </span>
      </div>
      <p class="font-medium text-steel-1100">
        <span class="text-sm text-steel-700">VIN</span> {{ equipment.vin }}
      </p>
    </div>
    <div class="flex h-full flex-col">
      <div
        class="flex h-full flex-col justify-center px-4 py-2"
        :class="
          isSetEldExemptFeatureEnabled &&
          !isModal &&
          !equipment.is_eld_and_active &&
          !equipment.is_eld_exempt
            ? 'gap-2'
            : 'gap-4'
        "
      >
        <div class="grid grid-cols-4 text-left">
          <span class="col-span-1 text-sm font-normal text-steel-700"
            >Truck #</span
          >
          <span
            class="col-span-3 truncate text-sm font-medium text-steel-900"
            >{{ equipment.truck_number }}</span
          >
        </div>
        <div class="grid grid-cols-4 text-left">
          <span class="col-span-1 text-sm font-normal text-steel-700"
            >Vehicle</span
          >
          <span
            class="col-span-3 truncate text-sm font-medium text-steel-900"
            >{{
              [equipment?.year, equipment?.model, equipment?.make]
                .filter(Boolean)
                .join(' ')
            }}</span
          >
        </div>
        <div class="grid grid-cols-4 text-left">
          <span class="col-span-1 text-sm font-normal text-steel-700"
            >Source</span
          >
          <span
            class="col-span-3 truncate text-sm font-medium text-steel-900"
            >{{ equipment.source }}</span
          >
        </div>
        <div class="grid grid-cols-4 text-left">
          <span class="col-span-1 text-sm font-normal text-steel-700"
            >Last Seen</span
          >
          <span
            class="col-span-3 truncate text-sm font-medium text-steel-900"
            >{{ formattedLastSeen }}</span
          >
        </div>
      </div>
      <div class="flex flex-col gap-2 rounded-b-xl bg-steel-100 p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <span class="mr-2 text-sm text-steel-700">ELD</span>
            <img
              v-if="equipment.is_eld_and_active"
              src="@/assets/eld-connected-icon.svg"
              alt="ELD Connected Icon"
            />
            <svg
              v-else-if="equipment.is_eld_exempt"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M2.01389 8.51314C3.99541 6.35265 6.8391 5.00004 9.99993 5.00004C13.1608 5.00004 16.0044 6.35265 17.986 8.51314C18.297 8.85232 18.8242 8.8751 19.1634 8.56402C19.5026 8.25293 19.5253 7.72579 19.2143 7.38661C16.9303 4.8964 13.6471 3.33337 9.99993 3.33337C6.3528 3.33337 3.06952 4.8964 0.7856 7.38661C0.474516 7.72579 0.497294 8.25293 0.836476 8.56402C1.17566 8.8751 1.7028 8.85232 2.01389 8.51314Z"
                fill="#1F242E"
                fill-opacity="0.48"
              />
              <path
                d="M10 17.5C10.9205 17.5 11.6667 16.7538 11.6667 15.8334C11.6667 14.9129 10.9205 14.1667 10 14.1667C9.07953 14.1667 8.33333 14.9129 8.33333 15.8334C8.33333 16.7538 9.07953 17.5 10 17.5Z"
                fill="#1F242E"
                fill-opacity="0.48"
              />
              <path
                d="M9.9999 8.33337C8.01025 8.33337 6.20312 9.10703 4.85997 10.3716C4.52487 10.687 3.99747 10.6711 3.68199 10.336C3.36651 10.0009 3.38241 9.47354 3.71751 9.15806C5.35753 7.61405 7.56899 6.66671 9.9999 6.66671C12.4308 6.66671 14.6423 7.61405 16.2823 9.15806C16.6174 9.47354 16.6333 10.0009 16.3178 10.336C16.0023 10.6711 15.4749 10.687 15.1398 10.3716C13.7967 9.10703 11.9895 8.33337 9.9999 8.33337Z"
                fill="#1F242E"
                fill-opacity="0.48"
              />
              <path
                d="M7.29776 12.6615C8.02569 12.0407 8.96802 11.6667 9.99983 11.6667C11.0316 11.6667 11.974 12.0407 12.7019 12.6615C13.0521 12.9602 13.578 12.9184 13.8767 12.5682C14.1754 12.218 14.1336 11.6921 13.7834 11.3934C12.7652 10.525 11.4428 10 9.99983 10C8.55688 10 7.23441 10.525 6.21624 11.3934C5.86606 11.6921 5.8243 12.218 6.12295 12.5682C6.42161 12.9184 6.94759 12.9602 7.29776 12.6615Z"
                fill="#1F242E"
                fill-opacity="0.48"
              />
            </svg>
            <img
              v-else
              src="@/assets/eld-not-connected-icon.svg"
              alt="ELD Not Connected"
            />
            <p class="ml-1">
              {{
                equipment.is_eld_and_active
                  ? 'Connected'
                  : equipment.eld_exempt_category?.includes('pending')
                    ? 'Exemption Pending'
                    : equipment.eld_exempt_category?.includes('denied')
                      ? 'Exemption Denied'
                      : equipment.eld_exempt_category
                        ? 'Exempt'
                        : 'Not Connected'
              }}
            </p>
          </div>
          <div class="flex items-center">
            <span class="mr-2 text-sm text-steel-700">COI</span>
            <img
              :src="equipment.is_coi ? insuredIcon : coiNotFoundIcon"
              :alt="equipment.is_coi ? 'Insured Icon' : 'COI Not Found Icon'"
            />
            <p class="ml-1">
              {{ equipment.is_coi ? 'Insured' : 'Not Found' }}
            </p>
          </div>
        </div>
        <div
          v-if="
            isSetEldExemptFeatureEnabled &&
            !isModal &&
            !equipment.is_eld_and_active &&
            !equipment.is_eld_exempt
          "
          class="flex h-12 w-full justify-center"
        >
          <div class="flex w-full items-center justify-between gap-2">
            <MyLoadsDesktopButtonComponent
              button-class="flex-1 default-button rounded-[3px] px-3 py-1.5"
              button-text="Resolve Issues"
              text-class=" text-sm font-medium leading-10 tracking-tight"
              @button-click="emit('resolveIssuesClick', equipment)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
